import React, { useState, useMemo } from "react";
import { API } from "aws-amplify";
import NumberFormat from "react-number-format";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Hidden from "@material-ui/core/Hidden";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import RoomIcon from "@material-ui/icons/Room";
import ForumIcon from "@material-ui/icons/Forum";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import { fetchStore } from "../views/App";
import { formatCelular } from "../functions";
import { DELIVERY_OPTIONS, PAYMENT_OPTIONS } from "./Ventas/constants";
import ImageLoader from "./ImageLoader";
import logger from "../logger";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(4)
  },
  container: {
    padding: theme.spacing(4)
  },
  empty: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4)
  },
  logo: {
    width: "80%",
    height: "auto",
    margin: "0 auto"
  },
  flexRow: {
    display: "flex",
    flexDirection: "row",
    paddingBottom: theme.spacing(4)
  }
}));

const PedidoPrint = ({ id }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [pedido, setPedido] = useState(null);
  const [store, setStore] = useState(null);

  const fetchPedido = async () => {
    setLoading(true);

    try {
      const queryStringParameters = {
        pedido: id
      };

      const init = {
        queryStringParameters
      };

      const result = await API.get(process.env.REACT_APP_HUB_API, "/tickets/pedido", init);

      if (result.success) {
        setPedido(result.data);
        fetchStore(
          result.data.body_ticket.tienda.id,
          () => setLoading(true),
          () => setLoading(false),
          setStore
        );
      }
    } catch (err) {
      logger("PedidoPrint.fetchPedido.error", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useState(() => {
    if (id) {
      fetchPedido();
    }
  }, [fetchPedido]);

  const { delivery, payment } = useMemo(() => {
    let date = null;
    let today = null;
    let itemsCount = null;
    let delivery = null;
    let payment = null;

    if (pedido) {
      date = pedido.fecha_creacion_ticket;
      // today = isToday(date);

      itemsCount = `${pedido.body_ticket.carrito.items.length} Producto${
        pedido.body_ticket.carrito.items.length === 1 ? "" : "s"
      }`;

      delivery = {};

      if (pedido.body_ticket.despacho) {
        switch (pedido.body_ticket.despacho.tipo) {
          case DELIVERY_OPTIONS.delivery:
            const addressJson = pedido.body_ticket.despacho.deliveryAddress;
            let addressText = "";

            if (addressJson.description) {
              addressText = addressJson.description;
            } else {
              addressText = `${addressJson.addressName} ${addressJson.addressNumber}, ${addressJson.addressComuna}`;
            }

            delivery.icon = <LocalShippingIcon />;
            delivery.title = "Entrega a domicilio";
            delivery.subtitle = addressText;
            delivery.content = () => (
              <Typography variant="body1">
                {(pedido.body_ticket.despacho.deliveryService || {}).name}
                <br />
                {addressText}
                <br />
                {pedido.body_ticket.despacho.deliveryAddress2}
                {pedido.body_ticket.despacho.deliveryNote && (
                  <>
                    <br />
                    <br />
                    <em>{pedido.body_ticket.despacho.deliveryNote}</em>
                  </>
                )}
              </Typography>
            );
            break;
          case DELIVERY_OPTIONS.pickup:
            delivery.icon = <RoomIcon />;
            delivery.title = "Retiro en tienda";
            delivery.subtitle = "";
            delivery.content = () => (
              <>
                {(pedido.body_ticket.despacho.pickupOption === "other" ||
                  pedido.body_ticket.despacho.pickupNote) && (
                  <Typography variant="body1">
                    {pedido.body_ticket.despacho.pickupOption === "other" && (
                      <>
                        Retira {pedido.body_ticket.despacho.pickupPerson}
                        <br />
                      </>
                    )}
                    {pedido.body_ticket.despacho.pickupNote && (
                      <>
                        <br />
                        <em>{pedido.body_ticket.despacho.pickupNote}</em>
                      </>
                    )}
                  </Typography>
                )}
              </>
            );
            break;
          case DELIVERY_OPTIONS.convenience:
            delivery.icon = <ForumIcon />;
            delivery.title = "A convenir";
            delivery.subtitle = "";
            delivery.content = () => (
              <>
                {pedido.body_ticket.despacho.description && (
                  <Typography variant="body1">{pedido.body_ticket.despacho.description}</Typography>
                )}
              </>
            );
            break;
          case DELIVERY_OPTIONS.none:
            delivery.icon = <ReportProblemIcon />;
            delivery.title = "Sin delivery";
            delivery.subtitle = "";
            delivery.content = () => (
              <Typography variant="body1">Debe coordinar la entrega con el comprador</Typography>
            );
            break;
          default:
            break;
        }
      }

      if (pedido.body_ticket.carrito.payment) {
        payment = {};

        switch (pedido.body_ticket.carrito.payment.type) {
          case PAYMENT_OPTIONS.webpay.str:
          case PAYMENT_OPTIONS.onepay.str:
            const estado = pedido.body_ticket.carrito.payment.details.transaction
              ? pedido.body_ticket.carrito.payment.details.transaction.status === "success"
                ? { color: "initial", label: "Aprobado" }
                : pedido.body_ticket.carrito.status === "cancelado"
                ? { color: "error", label: "Rechazado" }
                : { color: "initial", label: "Pendiente" }
              : { color: "initial", label: "Pendiente" };

            payment.title =
              pedido.body_ticket.carrito.payment.type === PAYMENT_OPTIONS.webpay.str
                ? "WebPay Plus"
                : "OnePay";
            payment.icon = <CreditCardIcon />;
            payment.content = () => (
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="body2">Estado</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color={estado.color} variant="body2" align="right">
                    {estado.label}
                  </Typography>
                </Grid>
              </Grid>
            );
            break;
          case PAYMENT_OPTIONS.transferencia.str:
            payment.title = "Transferencia";
            payment.icon = <AccountBalanceIcon />;
            payment.content = () => {};
            break;
          default:
            payment.title = "";
            payment.icon = null;
            payment.subtitle = "";
            payment.content = () => <></>;
            break;
        }
      }
    }

    return { date, today, itemsCount, delivery, payment };
  }, [pedido]);

  const getComision = () => {
    const comision = pedido.body_ticket.carrito.payment.comision
      ? pedido.body_ticket.payment.comision.valor_parametro
      : pedido.comision;

    const totalPedido = pedido.body_ticket.pago.total;
    const subtotal = (comision * totalPedido) / 100;
    const iva = (subtotal * 19) / 100;

    return subtotal + iva;
  };

  const Productos = () => {
    return (
      <>
        {pedido.body_ticket.carrito.items.map((item, index) => (
          <Grid container item xs={12} key={`item-${index}`}>
            <Grid item xs={6}>
              <Typography variant="h6">{item.product.name}</Typography>
              <Typography className="mb-2" color="textSecondary" variant="body1">
                {item.quantity > 1 && (
                  <>
                    {" ("}
                    <NumberFormat
                      value={item.price}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="$"
                      displayType={"text"}
                    />
                    {" x "}
                    {item.quantity}
                    {")"}
                  </>
                )}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6" align="right">
                <NumberFormat
                  value={item.total}
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix="$"
                  displayType={"text"}
                />
              </Typography>
            </Grid>
          </Grid>
        ))}
      </>
    );
  };

  const Totales = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Divider className="mb-2" />
        </Grid>
        {pedido.body_ticket.pago.subtotal !== pedido.body_ticket.pago.total && (
          <>
            <Grid item xs={6}>
              <Typography variant="body1">Subtotal</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right">
                <NumberFormat
                  value={pedido.body_ticket.pago.subtotal || 0}
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix="$"
                  displayType={"text"}
                />
              </Typography>
            </Grid>
          </>
        )}
        {pedido.body_ticket.despacho &&
          pedido.body_ticket.despacho.tipo === DELIVERY_OPTIONS.delivery && (
            <>
              <Grid item xs={6}>
                <Typography variant="body1">Despacho</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1" align="right">
                  {pedido.body_ticket.carrito.payment.details.deliveryFee > 0 ? (
                    <NumberFormat
                      value={pedido.body_ticket.carrito.payment.details.deliveryFee}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="$"
                      displayType={"text"}
                    />
                  ) : (
                    "Gratis"
                  )}
                </Typography>
              </Grid>
            </>
          )}
        <Grid item xs={6}>
          <Typography variant="body1">Comisión</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1" align="right">
            <NumberFormat
              value={getComision()}
              decimalScale={0}
              thousandSeparator="."
              decimalSeparator=","
              prefix="-$"
              displayType={"text"}
            />
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6">
            <strong>Total</strong>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6" align="right">
            <strong>
              <NumberFormat
                value={pedido.body_ticket.pago.total || 0}
                thousandSeparator="."
                decimalSeparator=","
                prefix="$"
                displayType={"text"}
              />
            </strong>
          </Typography>
        </Grid>
      </Grid>
    );
  };

  if (!pedido || !store) {
    return null;
  }

  return (
    <>
      {loading ? (
        <div className={classes.empty}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <Container maxWidth="md">
          <Paper className={classes.paper}>
            <Grid container spacing={2}>
              <Grid item xs={4} sm={2}>
                {store && store.logo && store.logo.key ? (
                  <ImageLoader
                    imgKey={store.logo.key}
                    className={clsx(classes.logo, "logoUploader")}
                    avatar
                    variant="circle"
                    alt="Logo Tienda"
                    skeletonVariant="circle"
                    skeletonClassName={classes.logo}
                  />
                ) : (
                  <Avatar
                    className={classes.logo}
                    alt="Logo Tienda"
                    src={`${process.env.PUBLIC_URL}/assets/images/logo_cf.png`}
                  />
                )}
              </Grid>
              <Grid item xs={8} sm={4}>
                <Typography variant="h5">
                  <strong>{pedido.body_ticket.tienda.nombre}</strong>
                </Typography>
                {store.body.locations[0] && (
                  <>
                    <Typography variant="body1">{store.body.locations[0].name}</Typography>
                    <Typography variant="body1">
                      {store.body.locations[0].address.description}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="h6">
                  <strong>{`${pedido.body_ticket.cliente.primer_nombre} ${pedido.body_ticket.cliente.apellido_paterno}`}</strong>
                </Typography>
                <Typography variant="body1">
                  {formatCelular(pedido.body_ticket.cliente.telefono)}
                </Typography>
                <Typography variant="body1">{pedido.body_ticket.cliente.email}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Hidden smUp>
                <Grid container item xs={12} sm={6}>
                  <Typography variant="h6" className="ml-2 mb-3">
                    <strong>Número de pedido: {pedido.folio_ticket}</strong>
                  </Typography>
                  {delivery && delivery.content && (
                    <Grid item xs={12} className={classes.flexRow}>
                      <span className="mx-2">{delivery.icon}</span>
                      <span>
                        <Typography variant="body1">
                          <strong>{delivery.title}</strong>
                        </Typography>
                        {delivery.content()}
                      </span>
                    </Grid>
                  )}
                  {payment && payment.content && (
                    <Grid item xs={12} className={classes.flexRow}>
                      <span className="mx-2">{payment.icon}</span>
                      <span>
                        <Typography variant="body1">
                          <strong>{payment.title}</strong>
                        </Typography>
                        {payment.content()}
                      </span>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </Grid>
              </Hidden>
              <Grid item xs={12} sm={6}>
                <Productos />
              </Grid>
              <Hidden xsDown>
                <Grid container item xs={12} sm={6}>
                  <Typography variant="h6" className="ml-2 mb-3">
                    <strong>Número de pedido: {pedido.folio_ticket}</strong>
                  </Typography>
                  {delivery && delivery.content && (
                    <Grid item xs={12} className={classes.flexRow}>
                      <span className="mx-2">{delivery.icon}</span>
                      <span>
                        <Typography variant="body1">
                          <strong>{delivery.title}</strong>
                        </Typography>
                        {delivery.content()}
                      </span>
                    </Grid>
                  )}
                  {payment && payment.content && (
                    <Grid item xs={12} className={classes.flexRow}>
                      <span className="mx-2">{payment.icon}</span>
                      <span>
                        <Typography variant="body1">
                          <strong>{payment.title}</strong>
                        </Typography>
                        {payment.content()}
                      </span>
                    </Grid>
                  )}
                </Grid>
              </Hidden>
              <Grid item xs={12} sm={6}>
                <Totales />
              </Grid>
            </Grid>
          </Paper>
        </Container>
      )}
    </>
  );
};

export default PedidoPrint;
